/**
 * Сводный дашборд остатков — главный экран казначея.
 *
 * Flow: опрос /api/treasury/dashboard раз в 30 секунд → три блока картины
 *       (остатки по категориям, перекрытие по каждому провайдеру, свободная
 *       ликвидность) → у каждого показателя рядом со значением назван повод,
 *       по которому ему нельзя верить целиком.
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл компонента;
 *   - api.js: loadDashboard.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает дашборд на экране;
 *   - src/SourcesPanel.jsx — разбор заданий загрузки, ссылка снизу;
 *   - docs/specs/BL-2026-08-15-3cd7-balances-dashboard.md — техспека.
 *
 * Границы: экран НИЧЕГО не меняет. Управляющих элементов здесь нет ни для
 * одной роли, и это не сокрытие кнопок — запрет держит сервер (requireRole),
 * а дашборд просто нечего менять.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import { downloadReport, loadBookClients, loadDashboard } from "./api";

/** Период опроса — тот же, что у панели источников. */
const POLL_MS = 30000;

/**
 * Порядок категорий на экране.
 *
 * Задаётся здесь, а не берётся из ответа: ответ идёт в порядке строк
 * выборки, и главный показатель продукта — обязательство перед клиентами —
 * оказывался третьим. Порядок чтения экрана это порядок важности, а не
 * порядок, в котором СУБД вернула строки.
 */
export const CATEGORY_ORDER = [
  "client_funds",
  "company_funds",
  "partner_funds",
  "unclassified",
  "non_production",
];

/** Состояния казначейского сигнала о доливке. */
const SIGNAL_TITLES = {
  raised: "сигнал поднят",
  watching: "наблюдение",
  cleared: "снят",
};

/**
 * Слово, которым называется величина, которой нет (BR-011).
 *
 * Значение одно на модуль: money возвращает именно его, а Amount именно с ним
 * сравнивает результат. Две копии строки разошлись бы молча — и пометка
 * «величина неизвестна» просто перестала бы ставиться, без единой ошибки.
 */
export const UNKNOWN = "неизвестно";

/**
 * Форматирует денежную величину.
 *
 * @param {string|null} amount — величина строкой либо null
 * @returns {string} число с разделителями либо «неизвестно»
 *
 * null — это НЕ ноль (BR-011): величина, которой нет, называется словом,
 * а не прочерком и не нулём. Прочерк читается как «пусто», ноль — как факт.
 *
 * Число получается разбором строки, и это не нарушение правила «деньги не
 * float» (LR-004): здесь величина только ФОРМАТИРУЕТСЯ, арифметики над ней
 * нет — все суммы посчитаны на сервере в точных типах. Порядок величин
 * (до 10^10 с двумя знаками) укладывается в точное представление double
 * с большим запасом.
 */
export function money(amount) {
  if (amount === null || amount === undefined) return UNKNOWN;
  // ПУСТАЯ СТРОКА — тоже «неизвестно», и проверка здесь обязательна.
  // Так приходят величины кастоди (DEC-045): занято у него — стоимость
  // бумаг, снимка позиций в системе нет, и сервер отдаёт "" по BR-011.
  // Number('') === 0, поэтому без этой строки экран показывал бы у Exante
  // ровный ноль занятого при 3,7 млн денег на его счетах — то самое
  // враньё нулём, которое запрещено на всех остальных слоях.
  if (typeof amount === "string" && amount.trim() === "") return UNKNOWN;
  const value = Number(amount);
  if (Number.isNaN(value)) return UNKNOWN;
  return value.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Доля в процентах русской раскладкой.
 *
 * @param {string|null} value — доля от сервера либо null
 * @returns {string} «0,1 %» либо тире, когда доли нет
 *
 * Тире, а не «0,0 %»: доля отсутствует при нулевом остатке, и ноль здесь
 * был бы утверждением «отнесено ноль процентов» вместо «доля не определена»
 * (BR-011).
 */
export function percent(value) {
  if (value === null || value === undefined || value === "") return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return "—";
  return `${number.toLocaleString("ru-RU", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;
}

/**
 * Величина в ячейке таблицы.
 *
 * @param {object} props
 * @param {string|number|null} props.value — величина из ответа API
 * @returns {JSX.Element} число либо пометка «величина неизвестна»
 *
 * Отсутствие величины показывается УРОВНЕМ «величина неизвестна», а не обычным
 * текстом: слово «неизвестно», набранное цветом нормы, читается как значение.
 * Ноль, который ЕСТЬ, пометки не получает — BR-011 читается в обе стороны,
 * и измеренный ноль не должен выглядеть пробелом в данных.
 */
export function Amount({ value }) {
  const text = money(value);
  if (text !== UNKNOWN) return <>{text}</>;
  return (
    <span className="state-unknown">
      <span className="state-mark" aria-hidden="true">
        ?
      </span>
      {UNKNOWN}
    </span>
  );
}

/**
 * Короткое имя типа контрагента — часть методики до первого двоеточия.
 *
 * @param {string} kindTitle — полная формулировка методики с сервера
 * @returns {string} короткое имя типа либо исходная строка целиком
 *
 * Словаря «тип → имя» здесь НЕТ намеренно: он был бы второй копией BR-030 —
 * набор типов и их названия живут в domain.ProviderKindTitle, и копия
 * разошлась бы с оригиналом молча. Сокращение же — операция над ТЕКСТОМ:
 * оно ничего не утверждает о типах, а при незнакомом типе показывает
 * серверную формулировку «не описан» как есть.
 *
 * Если серверный текст потеряет двоеточие, бейдж покажет строку целиком —
 * некрасиво, но правда; молчаливой подмены смысла не происходит.
 */
export function kindShort(kindTitle) {
  if (!kindTitle) return "";
  const cut = kindTitle.indexOf(":");
  return cut === -1 ? kindTitle : kindTitle.slice(0, cut);
}

/**
 * Перечень условий, по которым читаются величины контрагента (DEC-046, BR-030).
 *
 * @param {object} p — строка перекрытия из ответа API
 * @returns {Array<{id: string, label: string, text: string}>} пункты условий
 *
 * Методика стоит ПЕРВОЙ: это условие, действующее всегда, а оговорки —
 * частные. Ни одна оговорка не отбрасывается и не сокращается: потеря
 * оговорки означает, что неразобранное расхождение выдано за подтверждённый
 * остаток. Пустой перечень означает, что подвала у строки нет вовсе.
 */
export function providerTerms(p) {
  const out = [];
  if (p.kind_title)
    out.push({ id: "kind", label: "Методика.", text: p.kind_title });
  (p.notes || []).forEach((note, index) =>
    out.push({ id: `note-${index}`, label: "", text: note }),
  );
  return out;
}

/** Показывает момент в UTC — время системы едино (LR-005). */
export function formatMoment(iso) {
  if (!iso) return "—";
  return new Date(iso).toISOString().replace("T", " ").slice(0, 16) + " UTC";
}

/**
 * Признак и перечень поводов неполноты показателя.
 *
 * @param {boolean} complete — полон ли показатель
 * @param {Array} reasons — поводы из ответа API
 *
 * Поводы приходят с сервера уже словами: почему показатель неполон — это
 * доменное знание о том, из чего он считается, и вычислять его во фронтенде
 * значило бы завести вторую копию правила (BR-023).
 */
export function Incomplete({ complete, reasons }) {
  if (complete) return null;
  return (
    <div className="incomplete">
      <span className="badge badge-warn">неполный</span>
      <ul>
        {reasons.map((reason, index) => (
          <li key={`${reason.kind}-${index}`}>{reason.detail}</li>
        ))}
      </ul>
    </div>
  );
}

/** Человеческие названия торговых платформ. */
const PLATFORM_TITLES = {
  unity: "Unity",
  mt5: "MT5",
  unknown: "платформа не определена",
};

/**
 * Разрез клиентских денег по книгам A-book и B-book.
 *
 * @param {object} books — блок books из ответа API
 *
 * Стоит сразу под остатками и читается вместе с ними: остатки отвечают
 * «сколько чьих денег», разрез — «куда они отнесены».
 *
 * Названия книг и платформ приходят СЛОВАМИ оттуда же, откуда величины:
 * книга выводится из вида контрагента доменным правилом, а не из имени
 * (domain.BookOfProviderKind). Строка «платформа не определена» —
 * не пробел вывода: занятый объём межкомпанейского контура и кастоди
 * считается стоимостью позиций и к платформе не привязан.
 */
export function BooksBlock({ books }) {
  // Выбор разбора: книга и, если сузили, платформа внутри неё. null —
  // перечень закрыт. Хранится ЗДЕСЬ, а не в родителе: разбор относится
  // к карточкам этого блока и переживать переключение экранов не должен.
  const [выбор, setВыбор] = useState(null);

  if (!books || !books.rows || books.rows.length === 0) return null;

  // Платформы сводятся по обеим книгам, пока книга не выбрана: карточка
  // отвечает на вопрос «сколько клиентских денег отнесено на этой
  // платформе». Как только книгу выбрали, карточки платформ становятся
  // сужением ВНУТРИ неё — и показывают величину этой книги, иначе щелчок
  // по карточке давал бы перечень, не сходящийся с числом на ней.
  const книгиДляПлатформ = выбор
    ? books.rows.filter((r) => r.book === выбор.book)
    : books.rows;
  const поПлатформам = new Map();
  for (const r of книгиДляПлатформ) {
    for (const p of r.by_platform || []) {
      const было = поПлатформам.get(p.platform) || {
        amount: 0,
        clients: 0,
        providers: [],
      };
      поПлатформам.set(p.platform, {
        amount: было.amount + Number(p.amount_usd || 0),
        clients: было.clients + (p.clients || 0),
        providers: [...было.providers, ...(p.providers || [])],
      });
    }
  }

  // Щелчок по книге открывает её целиком и снимает прежнее сужение:
  // платформа предыдущей книги к новой не относится.
  const выбратьКнигу = (book) =>
    setВыбор((prev) =>
      prev && prev.book === book && !prev.platform
        ? null
        : { book, platform: "" },
    );
  const выбратьПлатформу = (platform) =>
    setВыбор((prev) =>
      prev
        ? {
            book: prev.book,
            platform: prev.platform === platform ? "" : platform,
          }
        : prev,
    );

  return (
    <section className="block">
      <h2>Книги: куда отнесены клиентские деньги</h2>
      <p className="muted block-legend">
        <strong>A-book</strong> — поток уходит внешним контрагентам.{" "}
        <strong>B-book</strong> — остаётся у компании: внутренняя книга и
        межкомпанейский контур группы. Разрез считается из того же занятого
        объёма, что и блок перекрытия ниже, — это он же, собранный по месту
        назначения. Щёлкните книгу — ниже появится перечень клиентов, из которых
        сложилась её величина; затем платформу, чтобы сузить перечень до неё.
      </p>

      {/* Книги, итог и платформы — ОДНИМ горизонтальным рядом. Прежде они
          стояли двумя рядами по три, и правая половина экрана пустовала:
          шесть карточек по 300 пикселей умещаются в ряд на любом широком
          окне. Карточки платформ помечены словом «платформа» — без него
          «Unity» в одном ряду с книгами читалось бы третьей книгой. */}
      <div className="cards books-cards">
        {books.rows.map((r) => (
          <article
            className={
              "card-metric card-pick" +
              (выбор && выбор.book === r.book ? " card-pick-on" : "")
            }
            key={r.book}
          >
            {/* Карточка открывается КНОПКОЙ, а не обработчиком на article:
                щелчок по div недоступен с клавиатуры и не объявляется
                экранному диктору, а перечень нужен всем, кто читает экран. */}
            <button
              type="button"
              className="card-pick-hit"
              aria-pressed={Boolean(выбор && выбор.book === r.book)}
              onClick={() => выбратьКнигу(r.book)}
            >
              <h3>
                {r.book_title}
                {r.providers && r.providers.length > 0 && (
                  <span className="muted">
                    {" "}
                    · контрагентов {r.providers.length}
                  </span>
                )}
              </h3>
              <p className="metric">
                {money(r.amount_usd)} <span className="unit">USD</span>
              </p>
              <p className="muted">
                {(r.providers || []).join(", ") || "состав пуст"}
              </p>
            </button>
          </article>
        ))}
        <article className="card-metric">
          <h3>Всего по книгам</h3>
          <p className="metric">
            {money(books.total_usd)} <span className="unit">USD</span>
          </p>
          <p className="muted">
            {/* Нераспределённое стоит ПРИ итоге: итог без него выглядит
                полным, а это те же клиентские деньги — просто без места
                назначения (BR-023). */}
            не отнесено ни к одной книге — {money(books.unallocated_usd)} USD
          </p>
        </article>

        {[...поПлатформам.entries()].map(([platform, v]) => {
          // Сузить можно только внутри выбранной книги, и только там,
          // где у величины есть клиенты: у строки «платформа не
          // определена» их нет по построению, и перечень был бы пуст.
          const сужаемо = Boolean(выбор) && platform !== "unknown";
          const включено = Boolean(выбор) && выбор.platform === platform;
          const внутри = (
            <>
              <h3>
                <span className="books-platform-mark">платформа</span>{" "}
                {PLATFORM_TITLES[platform] || platform}
                {выбор && (
                  <span className="muted"> · в пределах выбранной книги</span>
                )}
              </h3>
              <p className="metric">
                {money(v.amount.toFixed(2))} <span className="unit">USD</span>
              </p>
              <p className="muted">
                {/* «Платформа не определена» без имён — загадка, а не
                  объяснение: заказчик спросил, что это за величина, и вопрос
                  был справедлив. Подпись называет и ПРИЧИНУ, и НОСИТЕЛЕЙ. */}
                {platform === "unknown" ? (
                  <>
                    {v.providers.length > 0 && (
                      <strong>{v.providers.join(", ")}</strong>
                    )}
                    {v.providers.length > 0 && ". "}
                    Занятый объём этих контрагентов — стоимость их позиций, а не
                    доля от баланса клиента. У такой величины нет
                    клиента-источника, значит нет и платформы, на которой он
                    торгует. Это свойство методики, а не пробел данных.
                  </>
                ) : (
                  `клиентов ${v.clients}`
                )}
              </p>
            </>
          );
          return (
            <article
              className={
                "card-metric" +
                (сужаемо ? " card-pick" : "") +
                (включено ? " card-pick-on" : "")
              }
              key={platform}
            >
              {сужаемо ? (
                <button
                  type="button"
                  className="card-pick-hit"
                  aria-pressed={включено}
                  onClick={() => выбратьПлатформу(platform)}
                >
                  {внутри}
                </button>
              ) : (
                внутри
              )}
            </article>
          );
        })}
      </div>

      {выбор && (
        <BookClientsTable
          book={выбор.book}
          platform={выбор.platform}
          onClose={() => setВыбор(null)}
        />
      )}

      {!books.is_complete && (
        <div className="incomplete">
          <span className="badge badge-warn">неполный</span>
          <ul>
            {(books.reasons || []).map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Перечень клиентов, из которых сложилась величина карточки книги.
 *
 * @param {string} book — код книги: 'a_book' | 'b_book'
 * @param {string} platform — 'unity' | 'mt5' либо '' (книга целиком)
 * @param {Function} onClose — закрыть перечень
 *
 * Flow: щелчок по карточке → запрос /api/treasury/book-clients →
 *       строки, крупные сверху, и сверка суммы строк с карточкой.
 *
 * Перечень грузится ПО ЩЕЛЧКУ, а не вместе с дашбордом: строк бывает
 * двести пятьдесят, и опрос раз в тридцать секунд платил бы за них всем,
 * включая тех, кто карточку не открывал.
 */
export function BookClientsTable({ book, platform, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let живо = true;
    setData(null);
    setError(null);
    loadBookClients(book, platform)
      .then((r) => {
        if (живо) setData(r);
      })
      .catch((e) => {
        if (живо) setError(e.message);
      });
    return () => {
      живо = false;
    };
  }, [book, platform]);

  if (error) {
    return (
      <div className="book-clients">
        <p className="error">Перечень клиентов не загрузился: {error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="book-clients">
        <p className="muted">Загрузка перечня…</p>
      </div>
    );
  }

  const безКлиента = Number(data.without_client_usd || 0);

  return (
    <div className="book-clients">
      <div className="book-clients-head">
        <h3>
          Из чего сложилось: {data.book_title}
          {data.platform
            ? ` · ${PLATFORM_TITLES[data.platform] || data.platform}`
            : ""}
        </h3>
        <button type="button" className="link" onClick={onClose}>
          закрыть перечень
        </button>
      </div>

      {/* Сверка стоит НАД таблицей: читатель пришёл с карточки и первым
          делом спрашивает, сходится ли перечень с числом, по которому он
          щёлкнул. Часть без клиента-источника названа здесь же — иначе
          сумма строк оказалась бы меньше карточки без объяснения (BR-023). */}
      <p className="muted">
        Карточка — {money(data.scope_total_usd)} USD. В перечне {data.clients}{" "}
        {data.clients === 1 ? "клиент" : "клиентов"} на {money(data.total_usd)}{" "}
        USD.
        {безКлиента !== 0 && (
          <>
            {" "}
            Ещё {money(data.without_client_usd)} USD — без клиента-источника
            {(data.without_client_providers || []).length > 0 && (
              <> ({data.without_client_providers.join(", ")})</>
            )}
            : занятый объём этих контрагентов считается стоимостью их позиций, а
            не долей от баланса клиента, и в перечень попасть не может.
          </>
        )}
      </p>

      {data.rows.length === 0 ? (
        <p className="muted">Ни один клиент в эту величину не вошёл.</p>
      ) : (
        <div className="scroll-rows">
          <table>
            <thead>
              <tr>
                <th>Клиент</th>
                <th>Счета</th>
                <th>Платформа</th>
                <th className="num">Средства клиента</th>
                {/* База раскладки — не то же, что средства: из неё вычтено
                    измеренное другими механизмами (стоимость бумаг и позиций
                    контура, DEC-080). Доля считается ОТ НЕЁ, поэтому столбец
                    стоит рядом: без него читатель делил бы на средства
                    и получал бы другое число. */}
                <th className="num">База раскладки</th>
                <th className="num">Отнесено на книгу</th>
                <th className="num">Доля от базы</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.client_code}>
                  <td>
                    {r.client_name || r.client_code}
                    <span className="cell-note">{r.client_code}</span>
                  </td>
                  <td>{(r.accounts || []).join(", ")}</td>
                  <td>{PLATFORM_TITLES[r.platform] || r.platform}</td>
                  <td className="num">{money(r.funds)}</td>
                  <td className="num">{money(r.routable)}</td>
                  <td className="num">{money(r.amount_usd)}</td>
                  {/* Доли нет, когда остаток нулевой: деление на ноль даёт
                      не ноль, а бессмыслицу (BR-011). Проценты проходят
                      через русскую раскладку: сервер отдаёт «0.1», а рядом
                      в той же строке стоит «286 692,30» — две разные
                      десятичные запятые в одной строке читаются как опечатка. */}
                  <td className="num">{percent(r.share_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Блок остатков по категориям средств.
 *
 * @param {object} balances — блок balances из ответа API
 *
 * Тестовый и демонстрационный контур выводится последним и подписан как
 * не входящий в итоги: его величины на три порядка больше боевых.
 */
function BalancesBlock({ balances }) {
  const rank = (cat) => {
    const index = CATEGORY_ORDER.indexOf(cat.category);
    // Категория, не названная в порядке, уходит в конец, а не теряется:
    // новый вид средств должен появиться на экране сам, пусть и последним.
    return index === -1 ? CATEGORY_ORDER.length : index;
  };
  const ordered = [...balances.categories].sort((a, b) => rank(a) - rank(b));

  return (
    <section className="block">
      <h2>Остатки</h2>
      <div className="cards">
        {ordered.map((cat) => (
          <article
            key={cat.category}
            className={`card-metric ${cat.in_totals ? "" : "card-muted"}`}
          >
            <h3>{cat.category_title || cat.category}</h3>

            {cat.category === "client_funds" ? (
              <p className="metric">
                {money(cat.client_funds_usd)} <span className="unit">USD</span>
              </p>
            ) : (
              <p className="metric">
                {money(cat.equity_usd)} <span className="unit">USD</span>
              </p>
            )}

            <p className="muted">
              {cat.category === "client_funds"
                ? "обязательство перед клиентами; средства на счетах — "
                : "средства на счетах — "}
              {money(cat.equity_usd)} USD · счетов {cat.accounts}
            </p>

            <table className="currencies">
              <thead>
                <tr>
                  <th>Валюта</th>
                  <th className="num">На счетах</th>
                  {cat.category === "client_funds" && (
                    <th className="num">Из них клиентские</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {cat.by_currency.map((line) => (
                  <tr key={line.currency}>
                    <td>{line.currency}</td>
                    <td className="num">{money(line.equity)}</td>
                    {cat.category === "client_funds" && (
                      <td className="num muted">{money(line.client_funds)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Повод невхождения в итог приходит СЛОВАМИ с сервера
                (domain.FundsCategoryNote). Прежде здесь стояла одна подпись
                на всех — «это не деньги компании», — и для технических
                агрегатов она была неверна: счета принадлежат компании,
                деньгами не является их ОСТАТОК. */}
            {!cat.in_totals && cat.note && <p className="muted">{cat.note}</p>}

            <Incomplete complete={cat.is_complete} reasons={cat.reasons} />
          </article>
        ))}
      </div>
    </section>
  );
}

/**
 * Число столбцов таблицы перекрытия.
 *
 * Подвал условий растягивается на всю ширину через colSpan, и расхождение
 * с шапкой ломает раскладку таблицы молча — заметно это только глазами.
 * Тест сверяет константу со счётом <th> в отрисованной шапке.
 */
export const PROVIDER_COLUMNS = 8;

/**
 * Блок перекрытия по каждому провайдеру.
 *
 * @param {Array} providers — строки перекрытия из ответа API
 *
 * Отрицательный свободный остаток показывается КАК ЕСТЬ и подписывается
 * словами: это состояние, а не ошибка (BR-007), и цвет о нём не сообщает —
 * его не видит ни скринридер, ни человек с цветовой слепотой.
 *
 * # ТРИ УРОВНЯ ПОМЕТОК, А НЕ ОДИН
 *
 * Прежде один класс красил красным три разные по смыслу вещи: аварию
 * (BR-007), пробел данных (BR-011) и условие чтения величины (DEC-046).
 * Последнее — самое длинное и самое частое, поэтому блок читался аварией
 * целиком, а настоящая авария в нём была неотличима от нормы.
 *
 *   1. state-critical (BR-007, ▲) — занято больше депозита. Единственный
 *      красный в блоке.
 *   2. state-unknown  (BR-011, ?) — величины нет. Тот же цвет, что у пометки
 *      «неполный»: «данных нет» и «данных не хватает» — одна семья.
 *   3. reading-terms  (DEC-046, §) — как читать величину. Это НОРМА, и цвет
 *      у неё спокойный.
 *
 * Уровень несут слова и форма значка, а не только цвет: значки помечены
 * aria-hidden, и смысл для скринридера остаётся в словах.
 */
export function ProvidersBlock({ providers }) {
  const seen = new Set();
  const commonReasons = [];
  providers.forEach((p) =>
    p.reasons.forEach((reason) => {
      if (seen.has(reason.detail)) return;
      seen.add(reason.detail);
      commonReasons.push(reason);
    }),
  );

  return (
    <section className="block">
      <h2>Перекрытие по провайдерам</h2>
      {/* Что означают столбцы — НА ЭКРАНЕ, а не в голове читающего.
          Заказчику пришлось выписывать эти три определения словами, чтобы
          объяснить, что красный минус здесь не отрицательная маржа и не
          тестовый счёт. Раз объяснение понадобилось, его место в блоке. */}
      <p className="muted block-legend">
        <strong>Депозит</strong> — фактические деньги на счетах контрагента.{" "}
        <strong>Занято</strong> — клиентские балансы, отнесённые на него текущим
        конфигом маршрутизации. <strong>Свободный остаток</strong> — разность
        первого и второго. Минус означает, что фондирования на контрагенте
        меньше, чем отнесённых на него клиентских денег, — это состояние
        перекрытия, а не отрицательная маржа и не тестовый счёт.
      </p>
      <table className="providers">
        <thead>
          <tr>
            <th>Провайдер</th>
            {/*
              Тип стоит СРАЗУ за именем, до чисел. Величины разных типов
              означают разное (DEC-045, BR-030): у торгового LP занято —
              деньги под клиентов, у кастоди — стоимость бумаг, у зеркала —
              отражённая позиция. Столбец «Занято», прочитанный сверху вниз
              без пометки типа, складывает три разных предмета в один.
              Здесь стоит КОРОТКОЕ имя: полная формулировка методики в 160
              знаков разворачивала эту ячейку в шесть строк и растягивала
              всю строку таблицы. Целиком она никуда не делась — стоит первым
              пунктом подвала условий под строкой.
            */}
            <th>Тип контрагента</th>
            <th className="num">Депозит</th>
            <th className="num">Занято</th>
            <th className="num">Свободный остаток</th>
            <th className="num">Нехватка обеспечения</th>
            <th>Сигнал доливки</th>
            <th>Полнота</th>
          </tr>
        </thead>
        <tbody>
          {providers.map((p) => {
            const terms = providerTerms(p);
            return (
              // Контрагент занимает ПАРУ строк: величины и подвал условий.
              // Fragment, а не <tbody> на каждого: несколько tbody внутри
              // одной таблицы ломают сквозное выравнивание столбцов.
              <Fragment key={p.provider_code}>
                <tr
                  className={[
                    "provider-row",
                    terms.length > 0 ? "with-terms" : "",
                    p.is_critical ? "row-critical" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <td>{p.provider_title}</td>
                  <td>
                    {p.kind_title ? (
                      <span className="kind-badge">
                        {kindShort(p.kind_title)}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num">
                    <Amount value={p.deposit_fact} />
                  </td>
                  <td className="num">
                    <Amount value={p.occupied} />
                    {/*
                      Пометка «не рассчитано» отдельной строкой: пустая величина
                      и без того выводится как «неизвестно», но «неизвестно,
                      потому что данные запаздывают» и «неизвестно, потому что
                      считать не из чего» — разные утверждения.
                      ПРИЧИНУ здесь НЕ повторяем. Их две — источника величины нет
                      вовсе (кастоди) либо нет входа методики (зеркало без
                      известного депозита), — и обе приходят с сервера оговоркой,
                      которая стоит в подвале строки. Текст, вписанный сюда,
                      назвал бы только первую и врал бы про зеркало.
                    */}
                    {p.occupied_known === false && (
                      <span className="value-note state-unknown">
                        <span className="state-mark" aria-hidden="true">
                          ?
                        </span>
                        не рассчитано — причина в условиях чтения ниже
                      </span>
                    )}
                  </td>
                  <td className="num">
                    <Amount value={p.free_balance} />
                    {/*
                      Единственное место блока, где допустим красный (BR-007).
                      Прежде тем же классом красились и оговорки, и пробел
                      данных — и блок читался аварией целиком, а настоящая
                      авария в нём была неотличима от нормы.
                    */}
                    {p.is_critical && (
                      <span className="value-note state-critical">
                        <span className="state-mark" aria-hidden="true">
                          ▲
                        </span>
                        фондирования меньше, чем отнесено роутингом
                      </span>
                    )}
                  </td>
                  <td className="num">
                    <Amount value={p.coverage_gap} />
                  </td>
                  <td>
                    {p.signal ? (
                      <span
                        className={
                          p.signal.severity === "ok" ? "muted" : "warn"
                        }
                      >
                        {SIGNAL_TITLES[p.signal.state] || p.signal.state}
                        {p.signal.topup_amount &&
                        Number(p.signal.topup_amount) > 0
                          ? ` · долить ${money(p.signal.topup_amount)}`
                          : ""}
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td>
                    {p.is_complete ? (
                      <span className="muted">полный</span>
                    ) : (
                      <span
                        className="badge badge-warn"
                        title={p.reasons.map((r) => r.detail).join("; ")}
                      >
                        неполный
                      </span>
                    )}
                  </td>
                </tr>

                {/*
                  Условия чтения величины (DEC-046) — подвалом на всю ширину,
                  а не внутри ячейки имени. Оговорка в 200 знаков в столбце
                  шириной 140px это восемь строк, и числа переставали читаться
                  столбцом. Спрятать её за клик или подсказку нельзя: случай
                  PrimeXM — 509 399 по книге Unity при нуле на живом счёте,
                  расхождение не разобрано, и спрятанная оговорка выдала бы его
                  за подтверждённый остаток. Подвал — обычный <tr>: он на экране
                  всегда и целиком.
                */}
                {terms.length > 0 && (
                  <tr
                    className={`provider-terms ${p.is_critical ? "row-critical" : ""}`.trim()}
                  >
                    <td colSpan={PROVIDER_COLUMNS}>
                      <div className="reading-terms">
                        {/*
                          Контрагент назван в метке намеренно: ячейка с colSpan
                          не связана ни с одним заголовком столбца, и в линейном
                          чтении скринридером подвал остался бы без адресата.
                        */}
                        <p className="reading-terms-head">
                          <span className="state-mark" aria-hidden="true">
                            §
                          </span>
                          Условия чтения величины — {p.provider_title}
                        </p>
                        <ul>
                          {terms.map((term) => (
                            <li key={term.id}>
                              {term.label && (
                                <span className="terms-key">{term.label}</span>
                              )}
                              {term.text}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {commonReasons.length > 0 && (
        // Поводы перечисляются один раз под таблицей, а не повторяются
        // в каждой из восьми строк. Берётся ОБЪЕДИНЕНИЕ по всем провайдерам,
        // а не поводы первой строки: источники и доли у них общие, но валюта
        // без курса бывает у одного провайдера и отсутствует у соседнего,
        // и такой повод при чтении первой строки исчез бы с экрана.
        <Incomplete complete={false} reasons={commonReasons} />
      )}
    </section>
  );
}

/**
 * Блок свободной ликвидности — итог по всем провайдерам.
 *
 * @param {object} liquidity — блок liquidity из ответа API
 */
function LiquidityBlock({ liquidity }) {
  return (
    <section className="block">
      <h2>Свободная ликвидность</h2>
      <div className="cards">
        <article className="card-metric">
          <h3>Свободно всего</h3>
          <p className="metric">
            {money(liquidity.free_balance)} <span className="unit">USD</span>
          </p>
          <p className="muted">
            депозит {money(liquidity.deposit_fact)} · занято{" "}
            {money(liquidity.occupied)} · провайдеров{" "}
            {liquidity.providers_total}
            {liquidity.providers_critical > 0
              ? `, с дефицитом ${liquidity.providers_critical}`
              : ""}
          </p>
        </article>

        <article className="card-metric">
          <h3>Нехватка обеспечения</h3>
          <p className="metric">
            {money(liquidity.coverage_gap)} <span className="unit">USD</span>
          </p>
          <p className="muted">
            сколько не хватает, чтобы депозиты покрывали занятый объём
          </p>
        </article>

        <article className="card-metric">
          <h3>Не распределено между провайдерами</h3>
          <p className="metric">
            {money(liquidity.unallocated.client_funds)}{" "}
            <span className="unit">USD</span>
          </p>
          <p className="muted">
            средства {liquidity.unallocated.clients} клиентов, которые не
            удалось привязать к провайдеру
          </p>
        </article>

        <article className="card-metric">
          <h3>Непокрытая экспозиция</h3>
          <p className="metric">
            {money(liquidity.uncovered_exposure)}{" "}
            <span className="unit">USD</span>
          </p>
          <p className="muted">
            сознательно принимаемый риск: часть средств клиента, под которую
            обеспечение не держится
          </p>
        </article>
      </div>

      <Incomplete
        complete={liquidity.is_complete}
        reasons={liquidity.reasons}
      />
    </section>
  );
}

/**
 * Блок ручных контрагентов — величины без машинного источника (DEC-046).
 *
 * @param {object} manual — блок manual_entries из ответа API
 *
 * # ОТДЕЛЬНЫЙ БЛОК, А НЕ СТРОКИ В ТАБЛИЦЕ ПЕРЕКРЫТИЯ
 *
 * У ручной строки нет ни счёта, ни доли, ни методики занятости — значит нет
 * ни занятого объёма, ни свободного остатка. В таблице перекрытия она заняла
 * бы эти столбцы пустотой, а пустота в колонке «Свободный остаток» читается
 * как «свободно всё».
 *
 * Итог подписан «в свободный остаток не входит» прямо на экране: смотрящий
 * на две соседние суммы складывает их в уме, и удержать его от этого может
 * только надпись.
 */
function ManualEntriesBlock({ manual }) {
  if (!manual || !manual.entries) return null;

  return (
    <section className="block">
      <h2>Ручные контрагенты</h2>
      <table className="providers">
        <thead>
          <tr>
            <th>Строка</th>
            <th className="num">Величина</th>
            <th>Источник</th>
            <th>Кто ввёл</th>
            <th>Комментарий</th>
          </tr>
        </thead>
        <tbody>
          {manual.entries.map((e) => (
            <tr key={e.code}>
              <td>{e.title}</td>
              <td className="num">
                {/*
                  has_value приходит рядом с amount намеренно (техспека 2feb
                  §3.2): потребитель, забывший проверить null, показал бы
                  пустую строку нулём — ровно то, что DEC-046 отверг поимённо.
                */}
                {e.has_value ? (
                  money(e.amount)
                ) : (
                  <span className="state-unknown">
                    <span className="state-mark" aria-hidden="true">
                      ?
                    </span>
                    значение не заведено
                  </span>
                )}
              </td>
              <td>
                <span className="badge badge-warn">вручную</span>
              </td>
              <td>{e.entered_by}</td>
              <td className="muted">{e.comment}</td>
            </tr>
          ))}
          <tr>
            <td>
              <strong>Итого</strong>
              {/*
                Это условие чтения итога, а не сообщение о беде: ручная строка
                действительно существует и действительно не входит в свободный
                остаток. Красным она красилась лишь потому, что уровень
                задавался одним классом на все случаи сразу.
              */}
              <div className="reading-terms">
                <p className="reading-terms-head">
                  <span className="state-mark" aria-hidden="true">
                    §
                  </span>
                  Условие чтения итога
                </p>
                <ul>
                  <li>
                    справочно: в свободный остаток не входит — у ручной строки
                    нет методики занятости
                  </li>
                </ul>
              </div>
            </td>
            <td className="num">
              <strong>{money(manual.total)}</strong>
            </td>
            <td colSpan={3} className="muted">
              со значением {manual.with_value}, без значения{" "}
              {manual.without_value}
            </td>
          </tr>
        </tbody>
      </table>

      <Incomplete complete={manual.is_complete} reasons={manual.reasons} />
    </section>
  );
}

/**
 * Блок банковских счетов (карточка BL-2026-08-15-2024, техспека §3.3, §6.10).
 *
 * @param {object} bank — блок bank_accounts из ответа API; нет блока —
 *        нет и показа (сервер старше карточки)
 *
 * # ДВЕ ВЕЛИЧИНЫ РАЗДЕЛЬНО И НИ ОДНА — НЕ СВОБОДНЫЙ ОСТАТОК
 *
 * Собственные средства в банках и клиентские средства на банковских счетах
 * — разные деньги: вторые обеспечивают обязательство перед клиентами и
 * ликвидностью компании не являются. Сложенные в одну строку, они завысили
 * бы «сколько у компании денег» ровно на клиентские.
 *
 * В свободный остаток провайдеров блок не входит по той же причине, что
 * ручные строки: у банковского счёта нет методики занятости. Это сказано
 * надписью рядом с итогом — смотрящий на соседние суммы складывает их в уме.
 *
 * Итог `null` — «итога нет» (валюта без курса, BR-010), а не ноль: здесь
 * его показывает money() словом «неизвестно» с пометкой уровня.
 */
export function BankAccountsBlock({ bank }) {
  if (!bank) return null;
  const rows = bank.by_currency || [];

  return (
    <section className="block">
      <h2>Банковские счета</h2>
      <table className="providers">
        <thead>
          <tr>
            <th>Величина</th>
            <th className="num">{bank.base_currency || "USD"}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Собственные средства в банках</td>
            <td className="num">
              <Amount value={bank.own_total_usd} />
            </td>
          </tr>
          <tr>
            <td>
              Клиентские средства на банковских счетах
              <div className="muted cell-note">не ликвидность компании</div>
            </td>
            <td className="num">
              <Amount value={bank.client_total_usd} />
            </td>
          </tr>
          {bank.unknown_total_usd !== null &&
            bank.unknown_total_usd !== undefined &&
            Number(bank.unknown_total_usd) !== 0 && (
              <tr>
                <td>
                  Не размечено
                  <div className="muted cell-note">
                    принадлежность счёта не определена — в итоги не входит
                  </div>
                </td>
                <td className="num">
                  <Amount value={bank.unknown_total_usd} />
                </td>
              </tr>
            )}
          <tr>
            <td colSpan={2}>
              <div className="reading-terms">
                <p className="reading-terms-head">
                  <span className="state-mark" aria-hidden="true">
                    §
                  </span>
                  Условие чтения итога
                </p>
                <ul>
                  <li>
                    {bank.note ||
                      "в свободный остаток провайдеров не входит: у банковского счёта нет методики занятости"}
                  </li>
                </ul>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {rows.length > 0 && (
        <table className="currencies">
          <thead>
            <tr>
              <th>Валюта</th>
              <th className="num">Собственные</th>
              <th className="num">Клиентские</th>
              <th className="num">Не размечено</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.currency}>
                <td>{r.currency}</td>
                <td className="num">{money(r.own)}</td>
                <td className="num">{money(r.client)}</td>
                <td className="num">{money(r.unknown)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="muted">
        Счетов: {bank.accounts_total}
        {bank.accounts_without_statement > 0 &&
          `, без выписки: ${bank.accounts_without_statement}`}
        {bank.oldest_cut_at &&
          ` · самый старый срез выписки ${formatMoment(bank.oldest_cut_at)}`}
      </p>

      <Incomplete complete={bank.is_complete} reasons={bank.reasons || []} />
    </section>
  );
}

/**
 * Сводный дашборд остатков.
 *
 * Состояний три, как и у панели источников: загрузка, отказ и картина.
 * Отказ загрузки НЕ стирает прежнюю картину — устаревшие числа с честной
 * подписью полезнее пустого экрана.
 */
export default function DashboardPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // Состояние кнопки выгрузки держится отдельно от ошибки загрузки: сорвалась
  // выгрузка — картина на экране по-прежнему верна, и подменять её сообщением
  // об отказе значило бы соврать о ней.
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const exportXlsx = useCallback(async () => {
    setExporting(true);
    setExportError("");
    try {
      await downloadReport("/api/treasury/dashboard.xlsx");
    } catch (err) {
      setExportError(err.message);
      console.error("выгрузка не удалась", err);
    } finally {
      setExporting(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const report = await loadDashboard();
      setData(report);
      setError("");
    } catch (err) {
      setError(err.message);
      console.error("сводная картина не загрузилась", err);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  if (data === null && !error) return <p>Загрузка сводной картины…</p>;
  if (data === null) {
    return (
      <section className="block">
        <h2>Сводная картина</h2>
        <p className="error">Не загрузилась: {error}</p>
      </section>
    );
  }

  const incompleteCount =
    data.balances.categories.filter((c) => c.in_totals && !c.is_complete)
      .length +
    data.providers.filter((p) => !p.is_complete).length +
    (data.liquidity.is_complete ? 0 : 1) +
    // Банковский блок входит в общий признак полноты (техспека
    // BL-2026-08-15-2024 §3.3 п. 4) — значит, и в счётчик в шапке.
    (data.bank_accounts && !data.bank_accounts.is_complete ? 1 : 0);

  const unhealthy = data.sources.filter((s) => !s.is_healthy);

  return (
    <div className="dashboard">
      <header className="dashboard-head">
        <h1>Сводная картина</h1>
        <span className="muted">
          на {formatMoment(data.at)} · валюта отчётности {data.base_currency}
        </span>
        {incompleteCount > 0 && (
          <span className="badge badge-warn">
            неполных показателей: {incompleteCount}
          </span>
        )}
        <button type="button" onClick={exportXlsx} disabled={exporting}>
          {exporting ? "Собираем файл…" : "Выгрузить в Excel"}
        </button>
      </header>

      {exportError && (
        // Отказ выгрузки называется словами и рядом с кнопкой: нажатие,
        // после которого не произошло ничего, читается как поломка экрана.
        <p className="error">Выгрузка не удалась: {exportError}</p>
      )}

      {error && (
        // Картина осталась на экране, но она уже не «сейчас» — сказать об
        // этом обязательно: молча показывать старые числа хуже, чем не
        // показывать никаких.
        <p className="error">
          Картина не обновилась ({error}); показаны данные на{" "}
          {formatMoment(data.at)}.
        </p>
      )}

      {unhealthy.length > 0 && (
        <p className="notice">
          Источников с поводами для внимания: {unhealthy.length} из{" "}
          {data.sources.length} —{" "}
          {unhealthy
            .map((s) => `${s.title} (${s.problems.join(", ")})`)
            .join("; ")}
          . Разбор заданий — ниже, в состоянии источников.
        </p>
      )}

      <BalancesBlock balances={data.balances} />
      <BooksBlock books={data.books} />
      <ProvidersBlock providers={data.providers} />
      {/*
        Ручные контрагенты стоят МЕЖДУ перекрытием и ликвидностью: это
        такие же остатки у контрагентов, но посчитанные иначе, и место
        рядом с перекрытием говорит об этом само. Ниже ликвидности они
        читались бы как её расшифровка, чем не являются.
      */}
      <ManualEntriesBlock manual={data.manual_entries} />
      {/* Банковские счета — рядом с ручными строками и по тому же образцу:
          отдельный блок, в свободный остаток не входит (§3.3). */}
      <BankAccountsBlock bank={data.bank_accounts} />
      <LiquidityBlock liquidity={data.liquidity} />
    </div>
  );
}
